import React, { useState, useEffect } from 'react';
import { ArrowLeft, TrendingUp, Phone, MessageSquare, Mic, Brain, DollarSign, Users } from 'lucide-react';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:8000';

const getHeaders = () => ({
  'Authorization': `Bearer ${localStorage.getItem('cia_admin_token')}`,
  'Content-Type': 'application/json',
});

// Telnyx pricing (accurate as of July 2026)
const COSTS = {
  number_monthly: 1.00,        // $1/month per number
  number_acquisition: 1.00,    // $1 one-time
  inbound_per_min: 0.0045,     // $0.0045/min inbound (Telnyx Call Control)
  outbound_per_min: 0.0130,    // $0.013/min outbound (bridging to agent)
  sms_per_message: 0.0040,     // $0.004/SMS outbound
  carrier_lookup: 0.0015,      // $0.0015/lookup at signup
  whisper_per_min: 0.0060,     // $0.006/min (OpenAI Whisper API)
  claude_per_call: 0.0020,     // ~$0.002/call (Claude Haiku for classification)
  resend_per_email: 0.0000,    // Free on current tier
};

function formatCurrency(amount) {
  return `$${amount.toFixed(3)}`;
}

function formatDuration(seconds) {
  if (!seconds) return '0m';
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return m > 0 ? `${m}m ${s}s` : `${s}s`;
}

function CostBar({ label, cost, total, color }) {
  const pct = total > 0 ? (cost / total) * 100 : 0;
  return (
    <div style={{ marginBottom: 10 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
        <span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>{label}</span>
        <span style={{ fontSize: 12, fontWeight: 600, color }}>{formatCurrency(cost)}</span>
      </div>
      <div style={{ height: 4, background: 'var(--border)', borderRadius: 2 }}>
        <div style={{ height: 4, width: `${Math.min(pct, 100)}%`, background: color, borderRadius: 2 }} />
      </div>
    </div>
  );
}

function CustomerDetail({ customer, onBack }) {
  const [calls, setCalls] = useState([]);
  const [loading, setLoading] = useState(true);


  useEffect(() => {
    fetch(`${API_BASE}/admin/customer-costs/${customer.id}`, { headers: getHeaders() })
      .then(r => r.json())
      .then(data => { setCalls(data.calls || []); setLoading(false); })
      .catch(() => setLoading(false));
  }, [customer.id]);


  // Calculate costs from actual call data
  const totalDuration = calls.reduce((s, c) => s + (c.duration || 0), 0);
  const totalCalls = calls.length;
  const answeredCalls = calls.filter(c => (c.duration || 0) > 0).length;
  const missedCalls = totalCalls - answeredCalls;
  const voicemails = calls.filter(c => c.is_voicemail).length;
  // SMS: one notification per call + one missed call SMS per unanswered call
  const estimatedSMS = totalCalls + missedCalls;

  const costs = {
    number_monthly: COSTS.number_monthly,
    inbound: (totalDuration / 60) * COSTS.inbound_per_min,
    outbound: (totalDuration / 60) * COSTS.outbound_per_min,
    sms: estimatedSMS * COSTS.sms_per_message,
    whisper: (totalDuration / 60) * COSTS.whisper_per_min,
    claude: totalCalls * COSTS.claude_per_call,
    carrier_lookup: 1 * COSTS.carrier_lookup,
    acquisition: COSTS.number_acquisition,
  };

  const totalCost = Object.values(costs).reduce((s, v) => s + v, 0);
  const revenue = customer.plan === 'starter' ? 49 : customer.plan === 'growth' ? 99 : customer.plan === 'professional' ? 199 : 0;
  const margin = revenue - totalCost;
  const marginPct = revenue > 0 ? (margin / revenue) * 100 : 0;

  return (
    <div>
      <button
        onClick={onBack}
        style={{ display: 'flex', alignItems: 'center', gap: 8, background: 'none', border: 'none', color: 'var(--blue-light)', cursor: 'pointer', fontSize: 13, marginBottom: 20 }}
      >
        <ArrowLeft size={14} /> Back to all customers
      </button>

      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12, marginBottom: 24 }}>
        <div>
          <h2 style={{ fontSize: 18, fontWeight: 700 }}>{customer.business_name || customer.name}</h2>
          <div style={{ fontSize: 13, color: 'var(--text-muted)', marginTop: 4 }}>
            {customer.phone} · {customer.email} · {customer.plan || 'trial'} plan
          </div>
        </div>
        <div style={{
          padding: '10px 20px', borderRadius: 10,
          background: margin > 0 ? 'rgba(0,200,150,0.1)' : 'rgba(239,68,68,0.1)',
          border: `1px solid ${margin > 0 ? 'rgba(0,200,150,0.3)' : 'rgba(239,68,68,0.3)'}`,
          textAlign: 'center',
        }}>
          <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 4 }}>Monthly Margin</div>
          <div style={{ fontSize: 24, fontWeight: 800, color: margin > 0 ? '#00C896' : '#ef4444' }}>
            {margin > 0 ? '+' : ''}{formatCurrency(margin)}
          </div>
          <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>{marginPct.toFixed(0)}% margin</div>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12, marginBottom: 24 }}>
        {[
          { label: 'Revenue', value: `$${revenue}/mo`, icon: DollarSign, color: '#00C896' },
          { label: 'Total Cost', value: formatCurrency(totalCost), icon: TrendingUp, color: '#ef4444' },
          { label: 'Calls (Ans/Total)', value: `${answeredCalls}/${totalCalls}`, icon: Phone, color: '#3B82F6' },
          { label: 'Total Duration', value: formatDuration(totalDuration), icon: Mic, color: '#f59e0b' },
        ].map(({ label, value, icon: Icon, color }) => (
          <div key={label} className="card" style={{ padding: 16, textAlign: 'center' }}>
            <Icon size={18} color={color} style={{ marginBottom: 8 }} />
            <div style={{ fontSize: 20, fontWeight: 700 }}>{value}</div>
            <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>{label}</div>
          </div>
        ))}
      </div>


      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 24 }}>

        {/* Cost breakdown */}
        <div className="card" style={{ padding: 20 }}>
          <h3 style={{ fontSize: 14, fontWeight: 600, marginBottom: 4 }}>Cost Breakdown (This Month)</h3>
          <p style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 16 }}>
            Estimated from your call data × Telnyx/OpenAI rates. Duration must be &gt;0 for accurate voice costs.
          </p>
          <CostBar label="Number rental" cost={costs.number_monthly} total={totalCost} color="#6366f1" />
          <CostBar label="Inbound minutes" cost={costs.inbound} total={totalCost} color="#3B82F6" />
          <CostBar label="Outbound/bridge minutes" cost={costs.outbound} total={totalCost} color="#06b6d4" />
          <CostBar label="SMS notifications" cost={costs.sms} total={totalCost} color="#f59e0b" />
          <CostBar label="Whisper transcription" cost={costs.whisper} total={totalCost} color="#8b5cf6" />
          <CostBar label="Claude classification" cost={costs.claude} total={totalCost} color="#00C896" />
          <div style={{ borderTop: '1px solid var(--border)', marginTop: 12, paddingTop: 12, display: 'flex', justifyContent: 'space-between' }}>
            <span style={{ fontSize: 13, fontWeight: 600 }}>Total</span>
            <span style={{ fontSize: 13, fontWeight: 700, color: '#ef4444' }}>{formatCurrency(totalCost)}</span>
          </div>
        </div>

        {/* Revenue vs cost */}
        <div className="card" style={{ padding: 20 }}>
          <h3 style={{ fontSize: 14, fontWeight: 600, marginBottom: 16 }}>Revenue vs Cost</h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '10px 14px', background: 'rgba(0,200,150,0.08)', borderRadius: 8, border: '1px solid rgba(0,200,150,0.2)' }}>
              <span style={{ fontSize: 13 }}>Monthly Revenue</span>
              <span style={{ fontSize: 15, fontWeight: 700, color: '#00C896' }}>${revenue}.00</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '10px 14px', background: 'rgba(239,68,68,0.08)', borderRadius: 8, border: '1px solid rgba(239,68,68,0.2)' }}>
              <span style={{ fontSize: 13 }}>Monthly Cost</span>
              <span style={{ fontSize: 15, fontWeight: 700, color: '#ef4444' }}>{formatCurrency(totalCost)}</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '10px 14px', background: margin > 0 ? 'rgba(0,200,150,0.05)' : 'rgba(239,68,68,0.05)', borderRadius: 8, border: `1px solid ${margin > 0 ? 'rgba(0,200,150,0.2)' : 'rgba(239,68,68,0.2)'}` }}>
              <span style={{ fontSize: 13, fontWeight: 600 }}>Gross Margin</span>
              <span style={{ fontSize: 15, fontWeight: 700, color: margin > 0 ? '#00C896' : '#ef4444' }}>
                {margin > 0 ? '+' : ''}{formatCurrency(margin)} ({marginPct.toFixed(0)}%)
              </span>
            </div>
            <div style={{ fontSize: 12, color: 'var(--text-muted)', lineHeight: 1.6, marginTop: 4 }}>
              <div>Cost per call: {formatCurrency(totalCalls > 0 ? totalCost / totalCalls : 0)}</div>
              <div>Break-even calls/month: {Math.ceil(totalCost / (totalCalls > 0 ? totalCost / totalCalls : 1))}</div>
            </div>
          </div>
        </div>
      </div>

      {/* Call log */}
      <div className="card" style={{ padding: 0 }}>
        <div style={{ padding: '14px 20px', borderBottom: '1px solid var(--border)' }}>
          <h3 style={{ fontSize: 14, fontWeight: 600 }}>Call Log</h3>
        </div>
        {loading ? (
          <div style={{ padding: 20, textAlign: 'center', color: 'var(--text-muted)' }}>Loading...</div>
        ) : calls.length === 0 ? (
          <div style={{ padding: 20, textAlign: 'center', color: 'var(--text-muted)' }}>No calls yet</div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ minWidth: 600 }}>
              <thead>
                <tr>
                  <th>Caller</th>
                  <th>Duration</th>
                  <th>Type</th>
                  <th>Est. Cost</th>
                  <th>Date</th>
                </tr>
              </thead>
              <tbody>
                {calls.map(call => {
                  const dur = call.duration || 0;
                  const callCost = (
                    (dur / 60) * (COSTS.inbound_per_min + COSTS.outbound_per_min + COSTS.whisper_per_min) +
                    COSTS.claude_per_call +
                    COSTS.sms_per_message
                  );
                  return (
                    <tr key={call.id}>
                      <td style={{ fontSize: 13 }}>{call.caller_number || '—'}</td>
                      <td style={{ fontSize: 13, fontFamily: 'var(--mono)' }}>{formatDuration(dur)}</td>
                      <td>
                        <span style={{ fontSize: 11, padding: '2px 8px', borderRadius: 99, background: 'var(--bg-hover)', color: 'var(--text-secondary)' }}>
                          {call.call_type || 'unknown'}
                        </span>
                      </td>
                      <td style={{ fontSize: 13, color: '#ef4444', fontFamily: 'var(--mono)' }}>{formatCurrency(callCost)}</td>
                      <td style={{ fontSize: 12, color: 'var(--text-muted)' }}>
                        {new Date(call.created_at).toLocaleDateString()}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

export default function AdminCosts() {
  const [customers, setCustomers] = useState([]);
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState({ totalRevenue: 0, totalCost: 0, totalMargin: 0 });

  useEffect(() => {
    fetch(`${API_BASE}/admin/customers`, { headers: getHeaders() })
      .then(r => r.json())
      .then(data => {
        const custs = data.customers || [];
        setCustomers(custs);

        // Calculate summary
        const totalRevenue = custs.reduce((s, c) => {
          const r = c.plan === 'starter' ? 49 : c.plan === 'growth' ? 99 : c.plan === 'professional' ? 199 : 0;
          return s + r;
        }, 0);
        const totalCost = custs.length * (COSTS.number_monthly + COSTS.number_acquisition);
        setSummary({ totalRevenue, totalCost, totalMargin: totalRevenue - totalCost });
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, []);

  if (selected) return <CustomerDetail customer={selected} onBack={() => setSelected(null)} />;

  return (
    <div>
      <h2 style={{ fontSize: 18, fontWeight: 700, marginBottom: 4 }}>Cost & Revenue Dashboard</h2>
      <p style={{ fontSize: 13, color: 'var(--text-muted)', marginBottom: 20 }}>
        Per-customer cost breakdown and margin analysis
      </p>

      {/* Summary cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12, marginBottom: 24 }}>
        {[
          { label: 'Total MRR', value: `$${summary.totalRevenue}/mo`, color: '#00C896' },
          { label: 'Total Cost', value: `$${summary.totalCost.toFixed(2)}/mo`, color: '#ef4444' },
          { label: 'Gross Margin', value: `$${summary.totalMargin.toFixed(2)}/mo`, color: summary.totalMargin > 0 ? '#00C896' : '#ef4444' },
        ].map(({ label, value, color }) => (
          <div key={label} className="card" style={{ padding: 20, textAlign: 'center' }}>
            <div style={{ fontSize: 24, fontWeight: 800, color }}>{value}</div>
            <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 4 }}>{label}</div>
          </div>
        ))}
      </div>

      {/* Customer list */}
      <div className="card" style={{ padding: 0 }}>
        <div style={{ padding: '14px 20px', borderBottom: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h3 style={{ fontSize: 14, fontWeight: 600 }}>All Customers</h3>
          <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>{customers.length} total</span>
        </div>
        {loading ? (
          <div style={{ padding: 20, textAlign: 'center', color: 'var(--text-muted)' }}>Loading...</div>
        ) : (
          <table>
            <thead>
              <tr>
                <th>Customer</th>
                <th>Plan</th>
                <th>Revenue</th>
                <th>Est. Cost</th>
                <th>Margin</th>
                <th>Status</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {customers.map(c => {
                const revenue = c.plan === 'starter' ? 49 : c.plan === 'growth' ? 99 : c.plan === 'professional' ? 199 : 0;
                const cost = COSTS.number_monthly + COSTS.number_acquisition;
                const margin = revenue - cost;
                return (
                  <tr key={c.id} style={{ cursor: 'pointer' }} onClick={() => setSelected(c)}>
                    <td>
                      <div style={{ fontWeight: 500, fontSize: 13 }}>{c.business_name || c.name}</div>
                      <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>{c.phone}</div>
                    </td>
                    <td>
                      <span style={{
                        fontSize: 11, padding: '2px 8px', borderRadius: 99,
                        background: c.stripe_customer_id ? 'rgba(0,200,150,0.1)' : 'rgba(245,158,11,0.1)',
                        color: c.stripe_customer_id ? '#00C896' : '#f59e0b',
                      }}>
                        {c.plan || 'trial'}
                      </span>
                    </td>
                    <td style={{ fontSize: 13, color: '#00C896', fontWeight: 600 }}>${revenue}/mo</td>
                    <td style={{ fontSize: 13, color: '#ef4444' }}>{formatCurrency(cost)}/mo</td>
                    <td style={{ fontSize: 13, fontWeight: 600, color: margin > 0 ? '#00C896' : '#ef4444' }}>
                      {margin > 0 ? '+' : ''}{formatCurrency(margin)}
                    </td>
                    <td>
                      <span style={{
                        fontSize: 11, padding: '2px 8px', borderRadius: 99,
                        background: c.is_active ? 'rgba(0,200,150,0.1)' : 'rgba(239,68,68,0.1)',
                        color: c.is_active ? '#00C896' : '#ef4444',
                      }}>
                        {c.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td style={{ fontSize: 12, color: 'var(--blue-light)' }}>View →</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
